import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ContentWrapper from "../../../common/components/contentWrapper";

const labelSx = { fontWeight: 600, color: "#333", fontSize: "14px", mb: "6px" };
const requiredSx = { color: "red", ml: "2px" };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "6px" } };

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function NotificationForm() {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = "First Name is required";
    if (!form.lastName.trim()) newErrors.lastName = "Last Name is required";
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!EMAIL_REGEX.test(form.email.trim())) {
      newErrors.email = "Enter a valid email address";
    }
    return newErrors;
  };

  const handleOptIn = () => {
    const validationErrors = validate();
    if (!smsConsent) validationErrors.smsConsent = "You must agree to the SMS consent before opting in";
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1000);
  };

  const handleCancel = () => {
    setForm({ firstName: "", lastName: "", email: "", phone: "" });
    setErrors({});
    setSmsConsent(false);
  };

  if (submitted) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 5, px: 2 }}>
        <Card
          elevation={0}
          sx={{
            border: "1px solid #b2dfdb",
            borderRadius: "8px",
            backgroundColor: "#fff",
            width: "100%",
            maxWidth: 500,
          }}
        >
          <CardContent sx={{ p: 4, "&:last-child": { pb: 4 } }}>
            <Stack alignItems="center" spacing={1.5} sx={{ py: 3 }}>
              <CheckCircleOutlinedIcon sx={{ fontSize: 52, color: "#2e7d32" }} />
              <Typography sx={{ fontSize: "20px !important", fontWeight: 600, color: "#2e7d32" }}>
                Opt-in successfully!
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", pt: 5, px: 2 }}>
      <Card
        elevation={0}
        sx={{
          border: "1px solid #b2dfdb",
          borderRadius: "8px",
          backgroundColor: "#fff",
          width: "100%",
          maxWidth: 500,
        }}
      >
        <CardContent sx={{ p: 4, "&:last-child": { pb: 4 } }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography sx={labelSx}>
                First Name<span style={requiredSx}>*</span>
              </Typography>
              <TextField
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                placeholder="Enter First Name"
                variant="outlined"
                size="small"
                fullWidth
                error={!!errors.firstName}
                helperText={errors.firstName}
                sx={fieldSx}
              />
            </Box>

            <Box>
              <Typography sx={labelSx}>
                Last Name<span style={requiredSx}>*</span>
              </Typography>
              <TextField
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                placeholder="Enter Last Name"
                variant="outlined"
                size="small"
                fullWidth
                error={!!errors.lastName}
                helperText={errors.lastName}
                sx={fieldSx}
              />
            </Box>

            <Box>
              <Typography sx={labelSx}>
                Email <span style={requiredSx}>*</span>
              </Typography>
              <TextField
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter Email"
                variant="outlined"
                size="small"
                fullWidth
                error={!!errors.email}
                helperText={errors.email}
                sx={fieldSx}
              />
            </Box>

            <Box>
              <Typography sx={labelSx}>Phone Number</Typography>
              <TextField
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="(123)-456-7890"
                variant="outlined"
                size="small"
                fullWidth
                sx={fieldSx}
              />
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={smsConsent}
                    onChange={(e) => {
                      setSmsConsent(e.target.checked);
                      if (e.target.checked) setErrors((prev) => ({ ...prev, smsConsent: "" }));
                    }}
                    size="small"
                    sx={{ color: errors.smsConsent ? "error.main" : undefined, pt: 0 }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '13px', fontStyle: 'italic', opacity: '0.8', lineHeight: 1.5 }}>
                    By providing my phone number and clicking Opt-In, I consent to receive transactional SMS notifications, verification codes, and account alerts from WatchOps. Message and data rates may apply.
                  </Typography>
                }
                alignItems="flex-start"
                sx={{ mx: 0 }}
              />
              {errors.smsConsent && (
                <FormHelperText error sx={{ mt: 0, ml: '32px' }}>{errors.smsConsent}</FormHelperText>
              )}
            </Box>

            <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ pt: 0.5 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  borderRadius: "6px",
                  fontWeight: 500,
                  color: "#333",
                  borderColor: "#ccc",
                  "&:hover": { borderColor: "#aaa" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleOptIn}
                disabled={loading}
                disableElevation
                sx={{
                  textTransform: "none",
                  borderRadius: "6px",
                  fontWeight: 600,
                  minWidth: 100,
                  backgroundColor: "#1a2e4a",
                  "&:hover": { backgroundColor: "#162540" },
                  "&.Mui-disabled": { backgroundColor: "#1a2e4a", opacity: 0.8 },
                }}
              >
                {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Opt-In"}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

class NotificationComponent extends React.Component {
  getContentView() {
    return <NotificationForm />;
  }

  render() {
    return (
      <ContentWrapper contentView={this.getContentView()} {...this.props} />
    );
  }
}

export default NotificationComponent;
